import React, { FC, useState } from 'react'
import {
  Grid,
  GridColumn,
  GridToolbar,
  GridCellProps,
  GridItemChangeEvent,
  GridFilterChangeEvent,
} from '@progress/kendo-react-grid'
import { CommandCell } from './CommandCell'
import { addChange, addNew, discard, enterEdit, itemChange } from './GridServices'
import { cloneDeep } from 'lodash'
import { SelectionCell } from 'components/kendo/select/SelectionCell'
import { IntlProvider, LocalizationProvider, loadMessages } from '@progress/kendo-react-intl'
import trMessages from './language/gd-tr'
import enMessages from './language/gd-en'
import arMessages from './language/gd-ar'
import { useAppSelector } from 'app/hooks'
import { useTranslation } from 'react-i18next'
import { FixMeLater } from 'types'
import styled from 'styled-components'

loadMessages(enMessages, 'en-US')
loadMessages(trMessages, 'tr-TR')
loadMessages(arMessages, 'ar-AR')

interface IGridData {
  loading?: boolean
  datasource: FixMeLater
  onDataStateChange?: FixMeLater
  dataState: FixMeLater
  filterable?: boolean
  sortable?: boolean
  total?: number
  pageable?: boolean
  insertMode?: boolean
  editMode?: boolean
  key?: FixMeLater
  selectionMode?: boolean
  selectedList?: FixMeLater
  onInEditChange?: FixMeLater
  onFilterChange?: (event: GridFilterChangeEvent) => void
  onChangeSelected?: FixMeLater
  remove?: FixMeLater
  update?: FixMeLater
  selectedField?: string
  add?: FixMeLater
  selectable?: {
    enabled: boolean
    drag: boolean
    cell: boolean
    mode: string
  }
  style?: React.CSSProperties
}

const KGrid: FC<React.PropsWithChildren<IGridData>> = ({
  children,
  loading,
  datasource,
  key,
  total,
  dataState,
  filterable = false,
  sortable = false,
  pageable = false,
  insertMode = false,
  editMode = false,
  selectionMode = false,
  selectedList,
  onDataStateChange,
  onInEditChange,
  onChangeSelected,
  onFilterChange,
  remove,
  update,
  add,
  style,
}) => {
  const [orginalDataSource, setOrginalDataSource] = useState([])

  const notificationLanguage =
    useAppSelector((state) => state.auth.user.notificationLanguage) || 'tr'
  const { t } = useTranslation()

  return (
    <GridContainer>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
      <div>
        <LocalizationProvider
          language={
            notificationLanguage === 'tr'
              ? `${notificationLanguage}-${notificationLanguage?.toUpperCase()}`
              : notificationLanguage === 'ar'
              ? `${notificationLanguage}-${notificationLanguage?.toUpperCase()}`
              : 'en-US'
          }
        >
          <IntlProvider locale={notificationLanguage}>
            <Grid
              key={key}
              data={datasource}
              style={{...style, maxWidth: '100%', position: 'relative'}}
              pageable={
                pageable
                  ? {
                      buttonCount: 5,
                      pageSizes: [10, 20, 50, 100, 1000],
                      responsive: true,
                    }
                  : false
              }
              resizable={true}
              sortable={sortable}
              total={total}
              filterable={filterable}
              onFilterChange={onFilterChange}
              onDataStateChange={onDataStateChange}
              onItemChange={(e: GridItemChangeEvent) => onInEditChange(itemChange(datasource, e))}
              editField="inEdit"
              {...dataState}
            >
              {insertMode && (
                <GridToolbar>
                  <button
                    title={t('ADD')}
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                    onClick={() => onInEditChange(addNew(datasource))}
                  >
                    {t('ADD')}
                  </button>
                </GridToolbar>
              )}

              {selectionMode && (
                <GridColumn
                  filterable={false}
                  title=""
                  width="50px"
                  cells={{
                    data: (e: any) => (
                      <SelectionCell
                        isSelected={selectedList.some(
                          (dataItem: FixMeLater) => dataItem._id === e.dataItem._id,
                        )}
                        onChangeSelected={(e2: FixMeLater) =>
                          onChangeSelected(e.dataItem, e2.value)
                        }
                      />
                    )
                  }}
                />
              )}

              {children &&
                datasource &&
                [children].map((child) => {
                  return child
                })}

              {editMode && (
                <GridColumn
                  cells={{
                    data: (props: GridCellProps) => (
                      <CommandCell
                        {...props}
                        edit={(e: FixMeLater) => {
                          onInEditChange(enterEdit(datasource, e._id, true))
                          setOrginalDataSource(cloneDeep(datasource))
                        }}
                        remove={(e: FixMeLater) => {
                          remove({ ...e })
                        }}
                        add={async (e: FixMeLater) => {
                          delete e.inEdit
                          try {
                            const result = await add({ ...e })
                            if (result && result.data && result.data.insertedId) {
                              onInEditChange(addChange(datasource, result.data.insertedId))
                            } else {
                              //empty
                            }
                          } catch (error) {
                            // Hata durumunda ilgili işlemler
                          }
                        }}
                        discard={() => onInEditChange(discard(datasource))}
                        update={(e: FixMeLater) => {
                          delete e.inEdit
                          update({ ...e })
                        }}
                        cancel={() => onInEditChange(orginalDataSource)}
                        editField={'inEdit'}
                      />
                    )
                  }}
                  width="200px"
                />
              )}
            </Grid>
          </IntlProvider>
        </LocalizationProvider>
      </div>
    </GridContainer>
  )
}

export default KGrid

const GridContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #129fdb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`
